import {
  Checkbox,
  FlexBox,
  JoonUIColor,
  QuestInstance,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"

import useQuestBoardMultiSelectStore from "./useQuestBoardMultiSelectStore"
import useQuestBoardStore from "./useQuestBoardStore"
import { useQuestActionsModalStore } from "./viewQuestModal/QuestActionsModal"
import { useViewQuestModalStore } from "./viewQuestModal/useViewQuestModalStore"
import Card from "../../components/card/Card"
import { QuestSectionType } from "../../types/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { getQuestCardBorderColor, getQuestCardTabName } from "../../util/util"
import { QuestCardStatusOverlay } from "../me/CatchUpCompleteContent"

type Props = {
  questGroup: QuestInstance[]
  type: QuestSectionType
  index: number
}

export const QuestGroupCard = ({ questGroup, type, index }: Props) => {
  const { onOpen: openViewQuestModal } = useViewQuestModalStore()
  const { selectedUserId } = useQuestBoardStore()
  const { selectedQuestIds, toggleSelectedQuestId } =
    useQuestBoardMultiSelectStore()
  const { onOpen: openQuestActionsModal } = useQuestActionsModalStore()

  const [isHovered, setIsHovered] = useState(false)
  const [hoveredQuestCardInstanceId, setHoveredQuestCardInstanceId] = useState<
    null | number
  >(null)
  const [hoveredCheckboxInstanceId, setHoveredCheckboxInstanceId] = useState<
    null | number
  >(null)

  const isMandatory = questGroup[0]?.series?.mandatory

  const tabName = getQuestCardTabName(type, index)
  const borderColor = getQuestCardBorderColor(type, isMandatory)

  const minCoinRange = questGroup.reduce((acc, questInstance) => {
    return Math.min(acc, questInstance.series.redeemable_reward)
  }, Infinity)
  const maxCoinRange = questGroup.reduce((acc, questInstance) => {
    return Math.max(acc, questInstance.series.redeemable_reward)
  }, 0)

  const onClickQuestGroupCard = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_QUEST_DETAILS)
    openViewQuestModal(questGroup[0])
  }

  const onClickQuestCard = (questInstance: QuestInstance) => {
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_QUEST_STATUS_MODAL)
    openQuestActionsModal(questInstance)
  }

  return (
    <FlexBox
      direction="column"
      fullWidth
      wrap={false}
      style={{
        transform: isHovered ? "scale(1.015)" : "scale(1)",
        transition: "transform 0.2s",
      }}
    >
      {tabName && (
        <div
          style={{
            padding: `${SPACING.space0} ${SPACING.space2}`,
            background: borderColor,
            borderRadius: `${SPACING.space2} ${SPACING.space2} 0 0`,
            marginLeft: SPACING.space3,
            width: "fit-content",
            display: "flex",
            alignItems: "center",
            height: SPACING.space5,
          }}
        >
          <Typography
            variant="bodyXSmall"
            color={JoonUIColor.text.inverted}
            style={{ lineHeight: SPACING.space5 }}
          >
            {tabName}
          </Typography>
        </div>
      )}
      <button
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "relative",
          border: `1px solid ${borderColor}`,
          borderRadius: SPACING.space2,
          overflow: "hidden",
        }}
        onClick={() => onClickQuestGroupCard()}
      >
        <FlexBox
          direction="row"
          justify="space-between"
          align="center"
          fullWidth
          wrap={false}
        >
          <FlexBox
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            direction="column"
            gap={SPACING.space2}
            style={{
              padding: `${SPACING.space3} ${SPACING.space4}`,
              background: JoonUIColor.background.xlightGray,
            }}
          >
            {questGroup[0].series.reminder_time && (
              <Typography variant="bodyXSmall">
                {questGroup[0].series.reminder_time}
              </Typography>
            )}
            <FlexBox
              direction="row"
              align="center"
              justify="space-between"
              wrap={false}
            >
              <Typography
                variant="bodySmallBold"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {questGroup[0].series.title}
              </Typography>
              <FlexBox
                direction="row"
                align="center"
                justify="flex-end"
                gap={SPACING.space1}
                wrap={false}
                style={{ width: "fit-content", marginLeft: SPACING.space3 }}
              >
                <img
                  style={{ height: SPACING.space3 }}
                  src="/images/icons/coin-icon.png"
                  alt="coin"
                />
                <Typography variant="bodySmall">
                  {minCoinRange === maxCoinRange
                    ? minCoinRange
                    : `${minCoinRange}-${maxCoinRange}`}
                </Typography>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </FlexBox>
        {questGroup.map((questInstance) => (
          <FlexBox
            key={questInstance.id}
            onMouseEnter={() => setHoveredQuestCardInstanceId(questInstance.id)}
            onMouseLeave={() => setHoveredQuestCardInstanceId(null)}
            align="center"
            fullWidth
            wrap={false}
            style={{
              transform:
                hoveredQuestCardInstanceId === questInstance.id
                  ? "scale(1.015)"
                  : "scale(1)",
              transition: "transform 0.2s",
              position: "relative",
              background: "rgba(255, 255, 255, 0.6)",
              height: "fit-content",
              borderTop: `1px solid ${JoonUIColor.border.default}`,
            }}
          >
            {/* @ts-ignore */}
            {questInstance.temp_status && (
              <QuestCardStatusOverlay
                // @ts-ignore
                questStatus={questInstance.temp_status}
              />
            )}
            <Card
              title={""}
              subtitle={questInstance?.series?.reminder_time || ""}
              userIds={!selectedUserId ? [questInstance.user_id] : undefined}
              onClick={(e) => {
                e.stopPropagation()
                onClickQuestCard(questInstance)
              }}
              style={{
                border: "none",
                borderRadius: 0,
              }}
            />
            {type === QuestSectionType.ROUTINE && (
              <div
                onMouseEnter={() =>
                  setHoveredCheckboxInstanceId(questInstance.id)
                }
                onMouseLeave={() => setHoveredCheckboxInstanceId(null)}
                style={{
                  transform:
                    hoveredCheckboxInstanceId === questInstance.id
                      ? "scale(1.1)"
                      : "scale(1)",
                  transition: "transform 0.2s",
                }}
              >
                <Checkbox
                  name={questInstance.id.toString()}
                  selected={selectedQuestIds.includes(questInstance.id)}
                  onChange={(e) => {
                    e.stopPropagation()
                    toggleSelectedQuestId(questInstance.id)
                  }}
                  hideBorder
                  style={{
                    padding: `${SPACING.space3} ${SPACING.space4}`,
                    background: "transparent",
                  }}
                />
              </div>
            )}
          </FlexBox>
        ))}
      </button>
    </FlexBox>
  )
}
